import serverSideIcon from 'assets/images/integrations/server-side.svg';

import type { ConversionRule } from 'modules/pixel/pixelApiTypes';

import { CodeBlock } from '../CodeBlock';
import type { IntegrationContext, IntegrationSnippet, PixelIntegration } from './types';

/**
 * Server-to-server — fire any conversion via a POST to the pixel endpoint. Useful for
 * sandbox testing, backend-confirmed conversions, ad-blocker bypass, or any context where
 * the browser pixel isn't an option.
 * <p>
 * When the rule has {@code dspMappings} populated for Meta and/or Google, the per-rule
 * snippet ALSO includes optional {@code meta}/{@code google} blocks. The Lambda picks
 * those up, looks up the agency's stored DSP credentials in SSM, and fans the event out
 * server-side — no client-side fbq/gtag needed.
 */
export const serverSideIntegration: PixelIntegration = {
  id: 'server-side',
  label: 'Server-side (S2S)',
  description:
    'POST conversions directly to our pixel endpoint from your backend. Optional Meta CAPI + Google offline upload happen on our side using your connected accounts.',
  icon: <img src={serverSideIcon} alt="" className="h-4 w-4" />,
  renderSetup: (ctx) => (
    <div className="flex flex-col gap-3">
      <Step number={1} title="Send POST requests to the pixel endpoint">
        <p className="mb-2 text-base-xs text-primary-gray-500">
          Every conversion is just a JSON body POSTed to this URL — no SDK or pixel install needed.
          Works from any language (Node, Python, Ruby, curl, …).
        </p>
        <CodeBlock language="bash" code={ctx.s2sEndpoint} />
      </Step>
      <Step number={2} title="Pick a rule below and paste its snippet">
        <p className="text-base-xs text-primary-gray-500">
          Each rule below has a ready-to-fire snippet. Fill in any placeholder values (order total,
          currency, hashed user identifiers, etc.) on your side.
        </p>
      </Step>
      <Step number={3} title="Bonus: DSP fan-out happens server-side automatically">
        <p className="text-base-xs text-primary-gray-500">
          Rules wired to a Google conversion action or Meta custom conversion include an optional{' '}
          <code className="font-mono text-[11px] text-primary-gray-700">meta</code> /
          <code className="ml-1 font-mono text-[11px] text-primary-gray-700">google</code> block in
          the snippet. When present, we forward the event server-side to Meta Conversions API +
          Google offline conversion upload using the OAuth tokens you connected — no extra
          credentials needed in your code.
        </p>
        <p className="mt-2 text-base-xs text-system-warning-700">
          ⚠️ Meta access tokens expire every ~60–90 days. Re-connect your Meta account on the
          Settings page when you see CAPI errors in our reports.
        </p>
      </Step>
    </div>
  ),
  buildSnippetForRule: (rule, ctx) => buildServerSideSnippet(rule, ctx),
};

function Step({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-electric-100 text-[11px] font-semibold text-primary-electric-700">
          {number}
        </span>
        <h4 className="text-base-sm font-medium text-primary-gray-900">{title}</h4>
      </div>
      <div className="pl-7">{children}</div>
    </div>
  );
}

function buildServerSideSnippet(rule: ConversionRule, ctx: IntegrationContext): IntegrationSnippet {
  const agencyIdToken = ctx.agencyId ?? '{{AGENCY_ID}}';
  const mappings = rule.dspMappings ?? {};

  // Standard data_object — what we've always had. Kept first so existing direct-POST callers
  // see a familiar shape and the new blocks just feel like additions.
  const dataObjectLines: string[] = [`category: ${JSON.stringify(rule.category)}`];

  if (rule.subcategory) dataObjectLines.push(`subcategory: ${JSON.stringify(rule.subcategory)}`);
  const extractorKeys = rule.extractors ? Object.keys(rule.extractors) : [];

  for (const key of extractorKeys) {
    const placeholder =
      key === 'amount'
        ? '0           // your order total'
        : key === 'currency'
          ? '\'USD\'      // your order currency'
          : key === 'order_number'
            ? '\'\'        // your unique order id'
            : '\'\'';

    dataObjectLines.push(`${key}: ${placeholder}`);
  }
  const dataObject = dataObjectLines.map((l) => `      ${l},`).join('\n');

  // Build the optional DSP blocks. Each only appears when the rule has the corresponding
  // mapping populated — keeps the snippet minimal when a rule isn't wired to that DSP.
  const dspBlocks: string[] = [];

  if (mappings.meta) {
    // Meta CAPI block. agency_id drives SSM credential lookup on the Lambda side.
    const metaLines = [
      '  meta: {',
      `    agency_id: ${JSON.stringify(agencyIdToken)},`,
      ...(mappings.metaPixelId ? [`    pixel_id: ${JSON.stringify(mappings.metaPixelId)},`] : []),
      ...(mappings.metaEventName
        ? [`    event_name: ${JSON.stringify(mappings.metaEventName)},`]
        : []),
      `    custom_conversion_id: ${JSON.stringify(mappings.meta)},`,
      '  },',
    ];

    dspBlocks.push(metaLines.join('\n'));
  }

  if (mappings.google) {
    // Google offline upload. customer_id + conversion_action_id come from the gtag-style
    // send_to we stored (format: AW-<customer_id>/<action_id>). Parse defensively.
    const sendTo = mappings.google;
    const parsed = parseGoogleSendTo(sendTo);
    const googleLines = [
      '  google: {',
      `    agency_id: ${JSON.stringify(agencyIdToken)},`,
      `    customer_id: ${JSON.stringify(parsed.customerId ?? '{{GOOGLE_CUSTOMER_ID}}')},`,
      `    conversion_action_id: ${JSON.stringify(parsed.actionId ?? '{{CONVERSION_ACTION_ID}}')},`,
      '    gclid: gclid,                            // REQUIRED — capture on landing page from ?gclid=...',
      '  },',
    ];

    dspBlocks.push(googleLines.join('\n'));
  }

  // User identifiers for cross-device matching. Always shown — improves match quality for all
  // platforms, not just Meta CAPI. Hashed identifiers (em/ph) must be SHA256 of trimmed-lowercased
  // values; we don't hash for them — that has to happen on their side so PII never crosses our
  // network unhashed.
  const userDataNote = [
    '  // Optional: user identifiers improve cross-device matching',
    '  user_data: {',
    '    em: sha256(customer.email.trim().toLowerCase()),   // hashed email (SHA-256)',
    '    ph: sha256(customer.phone.replace(/[^0-9]/g, \'\')), // hashed phone (SHA-256), digits only',
    '    device_id: deviceId,             // mobile device ID (IDFA/GAID) if available',
    '    client_user_agent: userAgent,    // end user\'s browser/app user agent',
    '  },',
  ].join('\n');

  const customDataNote =
    rule.extractors && (rule.extractors.amount || rule.extractors.currency)
      ? [
          '  custom_data: {',
          '    value: orderTotal,         // numeric — e.g. 49.99',
          '    currency: orderCurrency,   // ISO 4217 — e.g. "USD"',
          '  },',
        ].join('\n')
      : null;

  const sections: string[] = [
    `  pixel_code: ${JSON.stringify(ctx.pixelCode)},`,
    '  event_id: uniqueOrderId,                    // REQUIRED for dedup with client-side pixel',
    '  event_time: Math.floor(Date.now() / 1000),  // unix seconds',
    '  event_source_url: pageUrl,                  // the page URL where conversion happened',
    '  document_data: { href: pageUrl },',
    '  referrer: referrerUrl,                      // the referring URL (if known)',
    '  user_ip: endUserIp,                         // REQUIRED for S2S: end user\'s device IP, NOT your server IP',
    '  gclid: gclid,                               // Google click ID from landing page URL (?gclid=...)',
    '  data_object: {',
    dataObject,
    '  },',
  ];

  sections.push(userDataNote);
  if (customDataNote) sections.push(customDataNote);
  if (dspBlocks.length > 0) sections.push(...dspBlocks);

  const code = [
    '// Replace these placeholders with actual values from your backend/request context',
    'const pageUrl = \'https://example.com/checkout\';      // page where conversion occurred',
    'const referrerUrl = \'\';                              // referring URL if known',
    'const endUserIp = req.headers[\'x-forwarded-for\'];    // end user\'s IP — NOT your server IP',
    'const uniqueOrderId = order.id;                      // unique ID for deduplication',
    'const userAgent = req.headers[\'user-agent\'];         // end user\'s user agent',
    'const deviceId = null;                               // IDFA/GAID if available (mobile apps)',
    'const gclid = session.gclid || null;                 // Google click ID captured on landing page',
    '',
    `fetch('${ctx.s2sEndpoint}', {`,
    '  method: \'POST\',',
    '  headers: { \'Content-Type\': \'application/json\' },',
    '  body: JSON.stringify({',
    sections.map((s) => indentLines(s, '  ')).join('\n'),
    '  }),',
    '});',
  ].join('\n');

  const note =
    dspBlocks.length > 0
      ? `Includes optional ${dspBlocks.length === 2 ? 'Meta + Google' : mappings.meta ? 'Meta CAPI' : 'Google offline upload'} fan-out — fires server-side via Cool Media's Lambda using your connected OAuth tokens.`
      : undefined;

  return { code, language: 'js', label: 'JavaScript (fetch)', note };
}

/**
 * Split a Google gtag {@code send_to} (e.g. {@code AW-1234567890/AbC123}) into the parts the
 * Lambda needs. Returns nulls on malformed input — snippet falls back to placeholders.
 */
function parseGoogleSendTo(sendTo: string): { customerId: string | null; actionId: string | null } {
  if (!sendTo) return { customerId: null, actionId: null };
  const match = sendTo.match(/^AW-(\d+)\/(.+)$/);

  if (!match) return { customerId: null, actionId: null };

  return { customerId: match[1], actionId: match[2] };
}

function indentLines(block: string, prefix: string): string {
  return block
    .split('\n')
    .map((l) => prefix + l)
    .join('\n');
}
